import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/services/apiClient";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { copyToClipboard } from "@/utils/clipboard";
import { StarIcon, UploadIcon } from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { ImportReviewsModal } from "./ImportReviewsModal";

interface Review {
  id: string;
  clientId: string;
  author: string;
  rating: string;
  reviewText: string;
  rephrasedText: string | null;
  ownerResponded: boolean;
  status: "new" | "used" | "cant_use";
  sentiment: "positive" | "negative";
  createdAt: string;
  updatedAt: string;
}

interface ReviewsPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const ReviewsPanel: React.FC<ReviewsPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [rephrasingIds, setRephrasingIds] = useState<Set<string>>(new Set());
  const [showOriginal, setShowOriginal] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filterStatus = searchParams.get("filter") || null;

  const handleFilterChange = (status: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set("filter", status);
    } else {
      params.delete("filter");
    }
    setSearchParams(params);
  };

  useEffect(() => {
    loadReviews();
  }, [clientId]);

  const loadReviews = async () => {
    try {
      setIsLoading(true);
      const fetchedReviews = await apiClient.get(`/client/${clientId}/reviews`);
      setReviews(fetchedReviews);
    } catch (error) {
      console.error("Failed to load reviews:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetStatus = async (reviewId: string, newStatus: "new" | "used" | "cant_use") => {
    try {
      await apiClient.put(`/client/${clientId}/reviews/${reviewId}/status`, {
        status: newStatus,
      });
      setReviews(
        reviews.map((r) =>
          r.id === reviewId ? { ...r, status: newStatus } : r
        )
      );
    } catch (error) {
      console.error("Failed to update review status:", error);
    }
  };

  const handleToggleSentiment = async (reviewId: string, currentSentiment: string) => {
    const newSentiment = currentSentiment === "positive" ? "negative" : "positive";
    try {
      await apiClient.put(`/client/${clientId}/reviews/${reviewId}/sentiment`, {
        sentiment: newSentiment,
      });
      setReviews(
        reviews.map((r) =>
          r.id === reviewId ? { ...r, sentiment: newSentiment as "positive" | "negative" } : r
        )
      );
    } catch (error) {
      console.error("Failed to update review sentiment:", error);
    }
  };

  const handleDeleteReview = (reviewId: string) => {
    setReviewToDelete(reviewId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reviewToDelete) return;

    try {
      await apiClient.delete(`/client/${clientId}/reviews/${reviewToDelete}`);
      setReviews(reviews.filter((r) => r.id !== reviewToDelete));
      setReviewToDelete(null);
    } catch (error) {
      console.error("Failed to delete review:", error);
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleRephraseReview = async (reviewId: string) => {
    setRephrasingIds((prev) => new Set(prev).add(reviewId));
    try {
      const updated = await apiClient.put(
        `/client/${clientId}/reviews/${reviewId}/rephrase`
      );
      setReviews(
        reviews.map((r) =>
          r.id === reviewId ? { ...r, rephrasedText: updated.rephrasedText } : r
        )
      );
      setShowOriginal((prev) => ({ ...prev, [reviewId]: false }));
    } catch (error) {
      console.error("Failed to rephrase review:", error);
    } finally {
      setRephrasingIds((prev) => {
        const next = new Set(prev);
        next.delete(reviewId);
        return next;
      });
    }
  };

  const toggleTextView = (reviewId: string) => {
    setShowOriginal((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  const handleCopy = async (text: string, reviewId: string) => {
    await copyToClipboard(text);
    setCopiedId(reviewId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredReviews = filterStatus
    ? reviews.filter((r) => r.status === filterStatus)
    : reviews;

  const newCount = reviews.filter((r) => r.status === "new").length;
  const usedCount = reviews.filter((r) => r.status === "used").length;
  const cantUseCount = reviews.filter((r) => r.status === "cant_use").length;

  return (
    <>
      <WorkspacePanel
        id="reviews"
        title="Reviews"
        icon={<StarIcon className="w-4 h-4" />}
        summary={newCount > 0 ? `${newCount} new` : "none new"}
        loading={isLoading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        subheader={
          <PanelFilterTabs
            options={[
              { id: "all", label: "All", count: reviews.length },
              { id: "new", label: "New", count: newCount },
              { id: "used", label: "Used", count: usedCount },
              { id: "cant_use", label: "Can't use", count: cantUseCount },
            ]}
            activeId={filterStatus ?? "all"}
            onChange={(id) => handleFilterChange(id === "all" ? null : id)}
          />
        }
        actions={
          <PanelHeaderAction
            label="Import"
            icon={<UploadIcon className="w-4 h-4" />}
            onClick={() => setImportOpen(true)}
          />
        }
      >
        <PanelList
          items={filteredReviews}
          keyFor={(r) => r.id}
          empty={
            <p className="text-sm text-gray-500 text-center py-8">
              {filterStatus ? `No ${filterStatus} reviews` : "No reviews yet. Use Import to add some."}
            </p>
          }
          renderItem={(review) => {
            const sentimentColors = review.sentiment === "positive"
              ? "border-l-emerald-300"
              : "border-l-rose-300";

            const statusColors = {
              new: "bg-blue-100 text-blue-700",
              used: "bg-gray-200 text-gray-600",
              cant_use: "bg-orange-100 text-orange-700",
            };

            // Full review + every control — shown in the row's view modal
            // (TD-004); the row itself stays a compact slotted line.
            const detail = (
              <div
                data-testid="review-detail"
                className={`border-l-2 pl-3 ${sentimentColors}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{review.author}</span>
                      <span className="text-xs text-gray-500">{review.rating}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${statusColors[review.status]}`}>
                        {review.status === "cant_use" ? "Can't Use" : review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                      </span>
                      {review.ownerResponded && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-sm">
                          Responded
                        </span>
                      )}
                      {review.rephrasedText && (
                        <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-sm">
                          AI Rephrased
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {review.rephrasedText && !showOriginal[review.id] ? (
                        <>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {review.rephrasedText}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopy(review.rephrasedText!, `${review.id}-rephrased`)}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                              {copiedId === `${review.id}-rephrased` ? "Copied!" : "Copy"}
                            </button>
                            <button
                              onClick={() => toggleTextView(review.id)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Show original
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {review.reviewText}
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopy(review.reviewText, `${review.id}-original`)}
                              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                              {copiedId === `${review.id}-original` ? "Copied!" : "Copy"}
                            </button>
                            {review.rephrasedText && (
                              <button
                                onClick={() => toggleTextView(review.id)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Show rephrased
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Sentiment toggle */}
                    <button
                      onClick={() => handleToggleSentiment(review.id, review.sentiment)}
                      className={`text-xs px-3 py-1 rounded ${
                        review.sentiment === "positive"
                          ? "bg-green-200 text-green-800 hover:bg-green-300"
                          : "bg-red-200 text-red-800 hover:bg-red-300"
                      }`}
                    >
                      {review.sentiment === "positive" ? "Positive" : "Negative"}
                    </button>

                    {/* Status buttons */}
                    <div className="flex gap-1">
                      {review.status !== "new" && (
                        <button
                          onClick={() => handleSetStatus(review.id, "new")}
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-sm hover:bg-blue-100"
                        >
                          New
                        </button>
                      )}
                      {review.status !== "used" && (
                        <button
                          onClick={() => handleSetStatus(review.id, "used")}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-sm hover:bg-gray-200"
                        >
                          Used
                        </button>
                      )}
                      {review.status !== "cant_use" && (
                        <button
                          onClick={() => handleSetStatus(review.id, "cant_use")}
                          className="text-xs px-2 py-1 bg-orange-50 text-orange-600 rounded-sm hover:bg-orange-100"
                        >
                          Can't Use
                        </button>
                      )}
                    </div>

                    <button
                      onClick={() => handleRephraseReview(review.id)}
                      disabled={rephrasingIds.has(review.id)}
                      className="text-xs px-2 py-1 bg-violet-50 text-violet-600 rounded-sm hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {rephrasingIds.has(review.id)
                        ? "Rephrasing..."
                        : review.rephrasedText
                          ? "Re-rephrase"
                          : "Rephrase"}
                    </button>

                    <button
                      onClick={() => handleDeleteReview(review.id)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            );

            return (
              <PanelListItem
                title={
                  <span className="inline-flex items-center gap-2">
                    {review.author}
                    <span className="text-xs font-normal text-gray-500">
                      {review.rating}
                    </span>
                  </span>
                }
                meta={
                  <>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-sm ${statusColors[review.status]}`}
                    >
                      {review.status === "cant_use"
                        ? "Can't Use"
                        : review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-sm ${
                        review.sentiment === "positive"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {review.sentiment === "positive" ? "Positive" : "Negative"}
                    </span>
                    {review.rephrasedText && (
                      <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-sm">
                        AI Rephrased
                      </span>
                    )}
                  </>
                }
                preview={review.rephrasedText ?? review.reviewText}
                trailing={
                  <span className="text-xs text-gray-400">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                }
                detailTitle={review.author}
                detail={detail}
              />
            );
          }}
        />
      </WorkspacePanel>

      <ImportReviewsModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        clientId={clientId}
        onImported={loadReviews}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setReviewToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};
